/**
 * Project team — WRITE request schemas (Pattern B+).
 *
 * These schemas ARE THE FIELD WHITELIST for `project_team` writes through
 * `apps/api/src/routes/projects/projects.ts`. Both objects are `.strict()`: zod silently
 * strips unknown keys otherwise, so a body carrying `project_id` would 200
 * with the key dropped instead of 400ing.
 *
 * DELIBERATELY ABSENT everywhere:
 *   - `project_id` — from the validated `:projectId` route param (pinned to
 *     the URL's org before the write).
 *   - `id`, `created_at` — database defaults.
 * `person_id` is immutable after creation, so the UPDATE schema omits it: a
 * different person is a remove plus an add.
 *
 * Column limits mirror `project_team`: `allocation_percentage int` with a
 * 0–100 CHECK, `start_date` / `end_date` plain `date` columns — and
 * `project_team_rates.hourly_rate numeric(10,2)` (at most 99,999,999.99, whole
 * cents; the rate lives in its own accounting-scoped table since org roles
 * step 3 — the API writes the member, then the rate). The date order
 * refine mirrors `project_team_dates_check`
 * (`end_date IS NULL OR start_date IS NULL OR end_date >= start_date`).
 */
import { z } from "zod";
import { calendarDateSchema } from "./dateWindow.js";
/** Longest single role label. */
export const PROJECT_TEAM_ROLE_MAX_LENGTH = 100;
/** Most roles one membership may carry. */
export const PROJECT_TEAM_MAX_ROLES = 50;
/** `numeric(10,2)`: eight integer digits, two decimals. */
export const PROJECT_TEAM_MAX_HOURLY_RATE = 99_999_999.99;
/**
 * 1+ role labels, each trimmed and 1–100 characters. Repeats (after the trim)
 * are dropped, first occurrence wins, so `["Crew", " Crew "]` stores `["Crew"]`.
 */
const rolesSchema = z
    .array(z.string().trim().min(1).max(PROJECT_TEAM_ROLE_MAX_LENGTH))
    .min(1, "At least one role is required")
    .max(PROJECT_TEAM_MAX_ROLES)
    .transform((roles) => [...new Set(roles)]);
const allocationSchema = z.number().int().min(0).max(100).nullable();
const hourlyRateSchema = z
    .number()
    .min(0)
    .max(PROJECT_TEAM_MAX_HOURLY_RATE)
    .multipleOf(0.01, "The hourly rate has at most two decimals")
    .nullable();
const dateSchema = calendarDateSchema.nullable();
/**
 * The date order, checked only when BOTH dates are present and non-null in
 * the body. On a PATCH that sends one end, the DB CHECK
 * `project_team_dates_check` is the backstop (the API maps its 23514 to the
 * coded 400 `invalid_date_range`).
 */
function datesOk(v) {
    if (v.start_date == null || v.end_date == null)
        return true;
    // `yyyy-MM-dd` strings compare in calendar order.
    return v.end_date >= v.start_date;
}
const DATES_MESSAGE = { message: "end_date must be on or after start_date", path: ["end_date"] };
/** POST /orgs/:orgId/projects/:projectId/team. */
export const createProjectTeamMemberSchema = z
    .object({
    person_id: z.string().uuid(),
    role: rolesSchema,
    allocation_percentage: allocationSchema.optional(),
    hourly_rate: hourlyRateSchema.optional(),
    start_date: dateSchema.optional(),
    end_date: dateSchema.optional(),
})
    .strict()
    .refine(datesOk, DATES_MESSAGE);
/**
 * PATCH /orgs/:orgId/projects/:projectId/team/:memberId — any subset, at least
 * one key. An explicit `null` clears a nullable field (rate, allocation, a
 * date); `role` can be replaced but never cleared.
 */
export const updateProjectTeamMemberSchema = z
    .object({
    role: rolesSchema.optional(),
    allocation_percentage: allocationSchema.optional(),
    hourly_rate: hourlyRateSchema.optional(),
    start_date: dateSchema.optional(),
    end_date: dateSchema.optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: "At least one field must be provided" })
    .refine(datesOk, DATES_MESSAGE);
//# sourceMappingURL=projectTeam.js.map